import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import { FaInstagram, FaPen, FaTrophy, FaUser } from 'react-icons/fa'

import { BeltVisual } from '@goodfellas/belt-visual'
import { Colors } from '@goodfellas/graduation'

import { goodfellasApi } from '../../apis'
import { normalizeBeltColor } from '../../functions/graduation'
import { useCompetitionStore, useStore } from '../../functions/store'
import EditProfileModal from '../public-profiles/EditProfileModal'
import { AddCompetition, CompetitionHistory } from './competition'

export default function DashboardProfile() {
  const { t: _t } = useTranslation('common')
  const user = useStore((state) => state)
  // `stats` is derived in the store from the matches that <CompetitionHistory>
  // loads below; it powers the summary counters in the sidebar.
  const { stats } = useCompetitionStore()
  const [loading, setLoading] = useState(true)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [profileId, setProfileId] = useState<string | null>(null)

  const fetchProfile = async () => {
    if (!user.phoneNumber) return
    try {
      setLoading(true)
      const response = await goodfellasApi.get('/evaluation/resolve/me')
      const data = response.data
      const profileId =
        data.identificator?.uuid ||
        data.phoneDigits ||
        data.phone?.payload?.number ||
        data.id ||
        data._id
      setProfileId(profileId)
      // Update global user state with all profile data
      user.setUser({
        ...user,
        name: data.name || user.name,
        belt: data.belt || user.belt,
        bio: data.bio || '',
        instagram:
          typeof data.instagram === 'object'
            ? data.instagram?.username || ''
            : data.instagram || '',
        avatar: data.avatar || null,
        achievements: data.achievements || [],
      })
    } catch (err) {
      console.debug('Error fetching profile data', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProfile()
  }, [user.phoneNumber])

  if (loading && !user.name) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    )
  }

  if (!user.name) {
    return (
      <div className="text-center py-20 text-white/40">
        <p>Não foi possível carregar os dados do perfil.</p>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Sidebar / Profile Card */}
        <div className="w-full md:w-1/3 lg:w-1/4">
          <div className="bg-gray-900 border border-white/10 rounded-3xl p-6 shadow-2xl backdrop-blur-sm">
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="w-40 h-40 rounded-full border-4 border-red-600 p-1 bg-gray-900 shadow-xl overflow-hidden">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-full h-full rounded-full object-cover my-0"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center">
                      <FaUser className="text-5xl text-white/20" />
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                  <BeltVisual
                    degree={user.degree}
                    color={normalizeBeltColor(user.belt) || Colors.White}
                    size="sm"
                  />
                </div>
              </div>

              <h1 className="text-3xl font-serif font-bold mb-1">
                {user.name}
              </h1>
              <p className="text-red-500 font-sans font-semibold mb-4">
                {user.group?.group || ''}
              </p>

              {user.instagram && (
                <a
                  href={`https://instagram.com/${user.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4 bg-white/5 px-4 py-2 rounded-xl"
                >
                  <FaInstagram />
                  <span>@{user.instagram}</span>
                </a>
              )}

              <button
                onClick={() => setIsEditModalOpen(true)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white transition-colors mb-6 px-6 py-2 rounded-xl border border-white/20"
              >
                <FaPen className="text-sm" />
                <span>Editar Perfil</span>
              </button>

              <div className="w-full pt-6 border-t border-white/5 space-y-4">
                <div className="flex justify-around">
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {stats.totalMatches}
                    </div>
                    <div className="text-xs text-white/40 uppercase">Lutas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{stats.wins}</div>
                    <div className="text-xs text-white/40 uppercase">
                      Vitórias
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{stats.losses}</div>
                    <div className="text-xs text-white/40 uppercase">
                      Derrotas
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-8 w-full">
          {/* Bio & Achievements */}
          <div className="bg-gray-900/50 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
            <h2 className="text-2xl font-serif font-bold mb-4">Sobre</h2>
            <p className="text-white/70 leading-relaxed mb-8">
              {user.bio || 'Nenhuma biografia disponível.'}
            </p>

            {user.achievements && user.achievements.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-white/40 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <FaTrophy className="text-yellow-500" />
                  Conquistas
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {user.achievements.map((achievement, i) => (
                    <li
                      key={i}
                      className="bg-white/5 px-4 py-3 rounded-xl text-sm border border-white/5"
                    >
                      {achievement}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Competition History — own profile: full CRUD (add/edit/delete) */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-serif font-bold">
                Histórico de Competição
              </h2>
            </div>

            <CompetitionHistory
              fighterUuid={profileId ?? undefined}
              phoneNumber={user.phoneNumber}
              showActions
              onAddClick={() => setIsAddModalOpen(true)}
            />
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <EditProfileModal
          profile={{
            id: profileId || user.phoneNumber,
            type: 'FIGHTER',
            name: user.name,
            avatar: user.avatar || null,
            bio: user.bio || '',
            instagram: user.instagram || '',
            achievements: user.achievements || [],
            belt: user.belt,
            degree: user.degree,
            gym: user.group?.group || '',
            phoneNumber: user.phoneNumber,
          }}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false)
          }}
        />
      )}

      {isAddModalOpen && (
        <AddCompetition
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => setIsAddModalOpen(false)}
        />
      )}
    </div>
  )
}
